import { useEffect, useState } from 'react'
import { customerApi } from '../lib/api'
import { useCustomerAuthStore } from '../stores/auth'

export interface CustomerModulesState {
  loaded: boolean
  modules: Record<string, boolean>
  error: string | null
}

/**
 * Laedt die effektiven Modul-Berechtigungen des aktuellen Kunden-Portal-Users
 * vom Backend (`GET /api/customer/me/modules`). Default-DENY: solange nichts
 * geladen ist oder ein Modul nicht explizit freigeschaltet wurde, gilt es als
 * verboten. Damit Navigation und API konsistent agieren — ein neu eingefuehrtes
 * Modul taucht nicht auf, bevor der Admin es bewusst freischaltet.
 */
export function useCustomerModules(): CustomerModulesState {
  const accessToken = useCustomerAuthStore((s) => s.accessToken)
  const [state, setState] = useState<CustomerModulesState>({ loaded: false, modules: {}, error: null })

  useEffect(() => {
    if (!accessToken) {
      setState({ loaded: false, modules: {}, error: null })
      return
    }
    let cancelled = false
    customerApi.get<{ modules: Record<string, boolean> }>('/me/modules')
      .then((res) => {
        if (cancelled) return
        setState({ loaded: true, modules: res.data?.modules ?? {}, error: null })
      })
      .catch((e: unknown) => {
        if (cancelled) return
        const msg = e instanceof Error ? e.message : 'Berechtigungen konnten nicht geladen werden'
        setState({ loaded: true, modules: {}, error: msg })
      })
    return () => { cancelled = true }
  }, [accessToken])

  return state
}

/**
 * Prueft mit Default-DENY, ob mindestens eines der angegebenen Module fuer den
 * aktuellen User aktiv ist. Liefert nur dann `true`, wenn ein Modul explizit
 * `true` ist. Solange `state.loaded` falsch ist, gilt grundsaetzlich verboten —
 * dadurch tauchen geschuetzte Nav-Eintraege erst auf, sobald die Berechtigungen
 * tatsaechlich geladen sind.
 */
export function hasAnyModule(state: CustomerModulesState, ids: string[]): boolean {
  if (!state.loaded) return false
  for (const id of ids) {
    if (state.modules[id] === true) return true
  }
  return false
}
